import { z } from 'zod';
import { checkFieldDefinitions, fieldDefinitionSchema, type FieldDefinition } from './fields';

// CUSTOM DATA — Dunea's metafields and metaobjects.
//
//   metafield   a typed value attached to a resource: product.custom.care_guide,
//               order.acme_gifts.message, app.custom.opening_hours
//   metaobject  an owner- or plugin-defined content type with fields and
//               entries: "team_member", "menu_item", "event", "faq"
//
// Namespaces decide who may write:
//   custom           the owner, the Dunea AI and the owner's panel
//   app--<slug>      reserved for the marketplace plugin with that listing slug
//   <first-party id> first-party plugins (shop, funnels…)
// Anyone with `data:read` can read any namespace; storefront visibility is
// opt-in per definition (`access.storefront: 'public'`).

/** Resources that carry metafields. `app` is the site itself (one per app). */
export const METAFIELD_OWNERS = [
  'app',
  'product',
  'variant',
  'collection',
  'order',
  'page',
  'funnel',
  'funnel_step',
  'metaobject',
] as const;

export type MetafieldOwner = (typeof METAFIELD_OWNERS)[number];

export const OWNER_NAMESPACE = 'custom';

/** The namespace a marketplace plugin owns. */
export function pluginNamespace(listingSlug: string): string {
  return `app--${listingSlug}`;
}

const namespace = z.string().regex(/^(custom|app--[a-z0-9-]{2,40}|[a-z][a-z0-9-]{1,40})$/);
const handle = z.string().regex(/^[a-z][a-z0-9_]{0,40}$/);

export const dataAccessSchema = z
  .object({
    /** Readable by the published site's runtime (useMetafield / useMetaobjects). */
    storefront: z.enum(['public', 'none']).default('none'),
    /** Who may write besides the namespace owner. */
    ownerWritable: z.boolean().default(true),
  })
  .default({ storefront: 'none', ownerWritable: true });

export const metafieldDefinitionSchema = z.object({
  ownerType: z.enum(METAFIELD_OWNERS),
  namespace: namespace.optional(),
  key: handle,
  name: z.string().min(1).max(80),
  description: z.string().max(300).optional(),
  /** The value's field definition (key/label are taken from key/name). */
  field: fieldDefinitionSchema.omit({ key: true, label: true }),
  access: dataAccessSchema,
  /** Show an editor for this metafield in the owner's panel on the resource. */
  pinned: z.boolean().default(true),
});

export type MetafieldDefinition = z.infer<typeof metafieldDefinitionSchema>;
export type MetafieldDefinitionInput = z.input<typeof metafieldDefinitionSchema>;

export const metaobjectDefinitionSchema = z.object({
  /** Type handle, e.g. "team_member". Plugin types are namespaced automatically. */
  type: handle,
  namespace: namespace.optional(),
  name: z.string().min(1).max(80),
  description: z.string().max(300).optional(),
  fields: z.array(fieldDefinitionSchema).min(1).max(40),
  /** The field shown as each entry's title in lists. */
  displayField: handle.optional(),
  access: dataAccessSchema,
});

export type MetaobjectDefinition = z.infer<typeof metaobjectDefinitionSchema>;
export type MetaobjectDefinitionInput = z.input<typeof metaobjectDefinitionSchema>;

/** What a plugin or theme manifest may declare; created on install, kept (not deleted) on uninstall. */
export const dataDeclarationsSchema = z
  .object({
    metafields: z.array(metafieldDefinitionSchema).max(40).default([]),
    metaobjects: z.array(metaobjectDefinitionSchema).max(20).default([]),
  })
  .superRefine((d, ctx) => {
    const seen = new Set<string>();

    for (const m of d.metafields) {
      const id = `${m.ownerType}.${m.key}`;

      if (seen.has(id)) {
        ctx.addIssue({ code: 'custom', path: ['metafields'], message: `duplicate metafield "${id}"` });
      }

      seen.add(id);

      if (m.ownerType === 'metaobject') {
        ctx.addIssue({
          code: 'custom',
          path: ['metafields'],
          message: 'metaobject fields belong in the metaobject definition, not as metafields',
        });
      }
    }

    const types = new Set<string>();

    for (const o of d.metaobjects) {
      if (types.has(o.type)) {
        ctx.addIssue({ code: 'custom', path: ['metaobjects'], message: `duplicate metaobject type "${o.type}"` });
      }

      types.add(o.type);
      checkFieldDefinitions(
        o.fields as FieldDefinition[],
        (message) => ctx.addIssue({ code: 'custom', path: ['metaobjects'], message }),
        `metaobject "${o.type}"`,
      );

      if (o.displayField && !o.fields.some((f) => f.key === o.displayField)) {
        ctx.addIssue({
          code: 'custom',
          path: ['metaobjects'],
          message: `metaobject "${o.type}" displayField "${o.displayField}" is not a field`,
        });
      }
    }
  });

export type DataDeclarations = z.infer<typeof dataDeclarationsSchema>;

/** Wire shapes (Plugin API, owner API, site runtime). */
export interface ApiMetafield {
  ownerType: MetafieldOwner;
  ownerId: string;
  namespace: string;
  key: string;
  type: string;
  value: unknown;
  updatedAt: string;
}

export interface ApiMetaobject {
  id: string;
  type: string;
  namespace: string;
  handle: string;
  fields: Record<string, unknown>;
  status: 'draft' | 'active';
  updatedAt: string;
}

export interface MetafieldSet {
  ownerType: MetafieldOwner;
  ownerId: string;
  namespace?: string;
  key: string;
  value: unknown;
}
